"use client";
import { useEffect, useState } from 'react';
import { cn } from '../lib/utils';

export type Heading = { id: string; text: string; level: number };

type TocProps = { headings: Heading[]; className?: string };

export function Toc({ headings, className }: TocProps) {
    const [activeId, setActiveId] = useState<string>('');

    useEffect(() => {
        if (headings.length === 0) return;

        const observer = new IntersectionObserver(
            entries => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        setActiveId(entry.target.id);
                    }
                });
            },
            { rootMargin: '-80px 0px -66%' }
        );

        headings.forEach(h => {
            const el = document.getElementById(h.id);
            if (el) observer.observe(el);
        });

        return () => observer.disconnect();
    }, [headings]);

    if (headings.length === 0) return null;

    return (
        <nav className={cn('sticky top-24 h-fit max-h-[calc(100vh-8rem)] overflow-y-auto', className)}>
            <div className="text-sm font-semibold mb-3 text-muted-foreground">Contents</div>
            <ul className="space-y-1 text-sm">
                {headings.map(h => (
                    <li key={h.id} style={{ paddingLeft: `${(h.level - 2) * 0.75}rem` }}>
                        <a
                            href={`#${h.id}`}
                            className={cn(
                                'block py-1 px-2 rounded-lg transition-colors',
                                'hover:bg-muted/50',
                                activeId === h.id
                                    ? 'text-primary font-medium bg-muted'
                                    : 'text-muted-foreground'
                            )}
                            onClick={e => {
                                e.preventDefault();
                                document.getElementById(h.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }}
                        >
                            {h.text}
                        </a>
                    </li>
                ))}
            </ul>
        </nav>
    );
}


